import { BLUE_400, GRAY_1000, WHITE } from 'constants/styling/theme'
import { Stack, Typography } from '@mui/material'

import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import { FC } from 'react'
import { LabProgress } from './labSdk.types'
import { resolveLabProgressStep } from './labProgressStep'
import styles from './LabRunBar.module.sass'
import { useLabProgressDisplay } from './useLabProgressDisplay.hook'
import { useTranslation } from 'react-i18next'

/**
 * The bar's label of the order's Develop stage (`edit`) reads "Prepare" (product decision 2026-10-08); the stage keeps
 * its key everywhere else (plan, mapping, `data-stage`).
 */
const segmentLabelStage = (stage: string) => stage === 'edit' ? 'prepare' : stage

const LABEL_SX = { fontSize: '1.2rem', lineHeight: '1.6rem', letterSpacing: '-0.2px' }

interface LabRunBarProps {
  progress: LabProgress
  /** Expected order stages (buildLabStagePlan + withLabPrepareStep); the live stage runs, earlier ones read as done */
  plan: string[]
}

/**
 * A Lab run's progress, docked in the view band above the photo — where Before | Compare | After sits, unusable while a
 * run goes — so nothing covers the photo: on our own infrastructure the photo itself shows what the run does. One row:
 * the plan's segments (done with a tick, running, to come), the running one naming its live sub-step from the feed
 * (`Detect · license plates`, `Generate · refining 5/8`), then the overall percentage and the time left. The running
 * segment advances on its stage clock between real events and nothing moves backwards (useLabProgressDisplay). Before
 * the order runs the percentage belongs to an order that has not started: a dash stands in for it.
 */
export const LabRunBar: FC<LabRunBarProps> = ({ progress, plan }) => {
  const { t } = useTranslation(['photo_studio_editing'])
  const { percent, segments, remainingMs, isPreparing, stageKey } = useLabProgressDisplay(progress, plan)

  const step = resolveLabProgressStep(isPreparing ? stageKey : progress.rawStage ?? progress.stage)
  const stepText = step ? t(`photo_studio_editing:lab.progress_step.${step.key}`, { ...step.params }) : null
  // No plan known: one segment on the overall percentage
  const rows = segments.length > 0 ? segments : [{ stage: stageKey, fill: percent / 100, isCurrent: true }]
  const stageLabel = (stage: string) => t(`photo_studio_editing:lab.progress_label.${segmentLabelStage(stage)}`, { defaultValue: stage })
  const current = rows.find((segment) => segment.isCurrent)
  const currentText = current ? [stageLabel(current.stage), stepText].filter(Boolean).join(' · ') : ''
  const roundedPercent = Math.round(percent)
  const etaSeconds = remainingMs && remainingMs > 1000 ? Math.round(remainingMs / 1000) : null

  return (
    <Stack
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={roundedPercent}
      aria-valuetext={isPreparing ? currentText : `${currentText}, ${roundedPercent}%`}
      className={styles.bar}
      flexDirection="row"
      alignItems="center"
      gap="1.6rem"
    >

      <Stack flexDirection="row" gap="1.2rem" sx={{ flex: '1 1 auto', minWidth: 0 }}>
        {rows.map((segment) => {
          const isDone = !segment.isCurrent && segment.fill >= 1
          const state = segment.isCurrent ? 'running' : isDone ? 'done' : 'pending'
          const hasStep = segment.isCurrent && !!stepText
          return (
            <Stack
              key={segment.stage}
              data-stage={segment.stage}
              data-state={state}
              gap=".7rem"
              sx={{ flex: `${hasStep ? 1.9 : 1} 1 0`, minWidth: 0, transition: 'flex-grow .35s ease' }}
            >

              <Stack flexDirection="row" alignItems="center" gap=".6rem" sx={{ minWidth: 0, height: '1.6rem' }}>

                <span className={[styles.dot, isDone && styles.done, segment.isCurrent && styles.running].filter(Boolean).join(' ')}>
                  {isDone && <CheckRoundedIcon sx={{ fontSize: '1rem', color: GRAY_1000 }} />}
                </span>

                <Typography noWrap sx={{ ...LABEL_SX, flexShrink: 0, maxWidth: '100%', color: WHITE, opacity: segment.isCurrent ? 1 : isDone ? 0.78 : 0.5, fontWeight: segment.isCurrent ? 550 : 400 }}>
                  {stageLabel(segment.stage)}
                </Typography>

                {hasStep && (
                  // Keyed by its text: each new sub-step fades in
                  <Typography key={stepText} noWrap className={styles.step} sx={{ ...LABEL_SX, minWidth: 0, color: BLUE_400 }}>
                    · {stepText}
                  </Typography>
                )}

              </Stack>

              <span className={styles.track}>
                {/* Inline width: it changes on every tick, and an `sx` value would mint a new CSS class each time */}
                <span className={styles.fill} data-fill style={{ width: `${segment.fill * 100}%` }} />
              </span>

            </Stack>
          )
        })}
      </Stack>

      <Stack
        alignItems="flex-end"
        gap=".1rem"
        sx={{ flex: 'none', minWidth: '11.2rem', padding: '0 .8rem 0 1.4rem', borderLeft: '1px solid rgba(255, 255, 255, 0.12)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
      >

        <Typography sx={{ fontSize: '1.5rem', lineHeight: 1.15, fontWeight: 600, letterSpacing: '-0.3px', color: WHITE }}>
          {isPreparing ? '—' : `${roundedPercent}%`}
        </Typography>

        {/* Always rendered: a line appearing would shift the percentage */}
        <Typography aria-hidden={etaSeconds === null} sx={{ fontSize: '1.1rem', lineHeight: 1.3, color: WHITE, opacity: 0.6 }}>
          {etaSeconds !== null ? t('photo_studio_editing:lab.progress.eta', { seconds: etaSeconds }) : ' '}
        </Typography>

      </Stack>

    </Stack>
  )
}
